import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { ArrowLeft, ArrowUpRight } from 'lucide-react'
import { useAuth } from '@workspace/public-utils'
import { Seo } from '@workspace/public-ui/components/Seo'
import BrandMark from '../components/BrandMark'
import LottomdHeader from '../components/layout/LottomdHeader'
import LottomdFooter from '../components/layout/LottomdFooter'

export default function LottomdLoginPage() {
  const [mobileNumber, setMobileNumber] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { login } = useAuth()
  const navigate = useNavigate()

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isLoading) return
    setError('')
    setIsLoading(true)
    try {
      await login(mobileNumber, password)
      navigate('/dashboard')
    } catch (err) {
      const detail = isAxiosError(err) ? err.response?.data?.detail : undefined
      setError(
        typeof detail === 'string'
          ? detail
          : '휴대폰 번호와 비밀번호를 확인하고 다시 시도해 주세요.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="md-site">
      <Seo title="로그인" canonicalPath="/auth/login" noindex />
      <LottomdHeader />
      <main className="md-main md-login" id="main-content" tabIndex={-1}>
        <div className="md-container md-login-grid">
          <div className="md-login-intro">
            <span className="md-kicker">다시 오신 걸 환영해요</span>
            <h1>
              나의 번호를 만나는
              <br />
              좋은 자리, 로또명당.
            </h1>
            <p>
              한 주의 기대를 담아두었어요.
              <br />
              로그인하고 나의 번호표를 꺼내보세요.
            </p>
            <Link to="/" className="md-text-link">
              <ArrowLeft size={16} aria-hidden="true" />
              홈으로 돌아가기
            </Link>
          </div>
          <section className="md-login-card" aria-labelledby="login-title">
            <header>
              <BrandMark />
              <h2 id="login-title">로그인</h2>
            </header>
            <form className="md-login-form" onSubmit={submit} aria-busy={isLoading}>
              {error && (
                <p className="md-form-error" role="alert" id="login-error">
                  {error}
                </p>
              )}
              <div className="md-field">
                <label htmlFor="mobile_number">휴대폰 번호</label>
                <input
                  id="mobile_number"
                  name="mobile_number"
                  type="tel"
                  autoComplete="username"
                  value={mobileNumber}
                  onChange={(event) => setMobileNumber(event.target.value)}
                  placeholder="010-1234-5678"
                  required
                  disabled={isLoading}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
              <div className="md-field">
                <label htmlFor="last4">비밀번호</label>
                <input
                  id="last4"
                  name="last4"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  required
                  disabled={isLoading}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
              <button type="submit" className="md-button" disabled={isLoading}>
                {isLoading ? '로그인 중…' : '나의 번호 만나러 가기'}
                <ArrowUpRight size={17} aria-hidden="true" />
              </button>
            </form>
            <p className="md-login-note">
              처음 이용하시나요?{' '}
              <Link to="/plans" className="md-text-link">
                구독 서비스 알아보기
              </Link>
            </p>
          </section>
        </div>
      </main>
      <LottomdFooter />
    </div>
  )
}
